import { supabase } from './supabaseClient';

/**
 * Storage Service for handling file uploads to Supabase Storage
 * This replaces base64 storage in database to reduce egress costs
 */

export interface UploadResult {
    url: string;
    path: string;
}

/**
 * Upload an image to Supabase Storage
 * @param file - File object or base64 string
 * @param userId - User ID for organizing files
 * @param folder - Folder name (e.g., 'portfolio', 'avatar', 'id-proof')
 * @returns Public URL of uploaded image
 */
export const uploadImage = async (
    file: File | string,
    userId: string,
    folder: 'portfolio' | 'avatar' | 'id-proof' | 'cover'
): Promise<UploadResult> => {
    try {
        let fileToUpload: File;
        let fileName: string;

        // Handle base64 string (for migration)
        if (typeof file === 'string') {
            // Convert base64 to File
            const base64Data = file.split(',')[1] || file;
            const mimeType = file.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
            const blob = base64ToBlob(base64Data, mimeType);
            const extension = mimeType.split('/')[1] || 'jpg';
            fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${extension}`;
            fileToUpload = new File([blob], fileName, { type: mimeType });
        } else {
            // Handle File object
            fileToUpload = file;
            const extension = file.name.split('.').pop() || 'jpg';
            fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${extension}`;
        }

        // Create path: userId/folder/filename
        const filePath = `${userId}/${folder}/${fileName}`;

        // Upload to Supabase Storage
        const { data, error } = await supabase.storage
            .from('worker-portfolios')
            .upload(filePath, fileToUpload, {
                cacheControl: '3600',
                upsert: false
            });

        if (error) {
            console.error('Upload error:', error);
            throw new Error(`Failed to upload image: ${error.message}`);
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('worker-portfolios')
            .getPublicUrl(filePath);

        console.log('✅ Image uploaded successfully:', publicUrl);

        return {
            url: publicUrl,
            path: filePath
        };
    } catch (error: any) {
        console.error('Error in uploadImage:', error);
        throw error;
    }
};

/**
 * Upload multiple images (for portfolio)
 */
export const uploadMultipleImages = async (
    files: (File | string)[],
    userId: string,
    folder: 'portfolio' | 'avatar' | 'id-proof' | 'cover'
): Promise<UploadResult[]> => {
    const uploadPromises = files.map(file => uploadImage(file, userId, folder));
    return Promise.all(uploadPromises);
};

/**
 * Delete an image from Supabase Storage
 */
export const deleteImage = async (filePath: string): Promise<void> => {
    try {
        const { error } = await supabase.storage
            .from('worker-portfolios')
            .remove([filePath]);

        if (error) {
            console.error('Delete error:', error);
            throw new Error(`Failed to delete image: ${error.message}`);
        }

        console.log('✅ Image deleted successfully:', filePath);
    } catch (error: any) {
        console.error('Error in deleteImage:', error);
        throw error;
    }
};

/**
 * Delete multiple images
 */
export const deleteMultipleImages = async (filePaths: string[]): Promise<void> => {
    try {
        const { error } = await supabase.storage
            .from('worker-portfolios')
            .remove(filePaths);

        if (error) {
            console.error('Delete multiple error:', error);
            throw new Error(`Failed to delete images: ${error.message}`);
        }

        console.log('✅ Images deleted successfully:', filePaths.length);
    } catch (error: any) {
        console.error('Error in deleteMultipleImages:', error);
        throw error;
    }
};

/**
 * Convert base64 to Blob
 */
const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
};

/**
 * Compress image before upload (optional but recommended)
 */
export const compressImage = async (file: File, maxWidth = 1200, quality = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Failed to compress image'));
                            return;
                        }

                        const compressedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });

                        console.log(`✅ Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${(compressedFile.size / 1024).toFixed(2)}KB`);
                        resolve(compressedFile);
                    },
                    'image/jpeg',
                    quality
                );
            };

            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
};

/**
 * Get file path from URL
 */
export const getFilePathFromUrl = (url: string): string | null => {
    try {
        // Extract path from Supabase Storage URL
        // Format: https://[project].supabase.co/storage/v1/object/public/worker-portfolios/[path]
        const match = url.match(/\/worker-portfolios\/(.+)$/);
        return match ? match[1] : null;
    } catch (error) {
        console.error('Error extracting file path:', error);
        return null;
    }
};

/**
 * Migrate base64 images to Storage (for existing data)
 */
export const migrateBase64ToStorage = async (
    base64Images: string[],
    userId: string,
    folder: 'portfolio' | 'avatar' | 'id-proof' | 'cover'
): Promise<UploadResult[]> => {
    console.log(`🔄 Migrating ${base64Images.length} base64 images to Storage...`);

    const results: UploadResult[] = [];

    for (const base64 of base64Images) {
        try {
            // Skip if already a URL
            if (base64.startsWith('http')) {
                results.push({ url: base64, path: '' });
                continue;
            }

            const result = await uploadImage(base64, userId, folder);
            results.push(result);
        } catch (error) {
            console.error('Failed to migrate image:', error);
            // Keep original base64 as fallback
            results.push({ url: base64, path: '' });
        }
    }

    console.log(`✅ Migration complete: ${results.length} images processed`);
    return results;
};
